import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { CheckSquare, FileText, Loader2, ShieldCheck, Zap } from "lucide-react";
import { IMAGES } from "@/data/kits";
import { useCheckout } from "@/lib/api";
import { scrollToId } from "@/lib/lenis";

const EASE = [0.16, 1, 0.3, 1];
const wrap = { hidden: {}, visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } } };
const lineV = {
  hidden: { y: "110%", opacity: 0 },
  visible: { y: "0%", opacity: 1, transition: { duration: 0.8, ease: EASE } },
};

const FILES = [
  "Customer-Intake-Authorization.pdf",
  "Repair-Estimate-Template.pdf",
  "One-Page-Warranty-Explainer.pdf",
  "Late-Payment-Escalation-Scripts.pdf",
  "Shop-Profit-Calculator.xlsx",
];

const STATS = [
  ["1,400+", "service bays running it"],
  ["$0", "monthly SaaS fees"],
  ["14", "shop-ready documents"],
  ["<60s", "delivery after checkout"],
];

export default function Hero() {
  const { buy, loading } = useCheckout();
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [6, -6]), { stiffness: 110, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-8, 8]), { stiffness: 110, damping: 16 });
  const cardX = useSpring(useTransform(mx, [0, 1], [-12, 12]), { stiffness: 90, damping: 18 });
  const cardY = useSpring(useTransform(my, [0, 1], [-8, 8]), { stiffness: 90, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section
      className="relative blueprint-grid overflow-hidden pt-28 lg:pt-32 pb-16"
      onMouseMove={onMove}
    >
      <div className="spotlight absolute inset-0 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-blaze font-semibold"
          >
            Industrial Admin Tooling // For Working Shops
          </motion.p>

          <motion.h1
            data-testid="hero-headline"
            variants={wrap}
            initial="hidden"
            animate="visible"
            className="mt-5 font-display font-black uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-7xl text-slate-50"
          >
            {["Run the shop.", "Not the", "paperwork."].map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span variants={lineV} className={`inline-block ${i === 2 ? "text-blaze" : ""}`}>
                  {l}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.7, ease: EASE }}
            className="mt-6 max-w-xl text-sm sm:text-base text-slate-400 leading-relaxed"
          >
            Three battle-tested kits of customer intake forms, work authorizations, estimate terms,
            warranty explainers, payment plans, and collection scripts — the admin backbone that
            stops money leaking out of your bays. Buy once. Download instantly. Own it forever.
          </motion.p>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85, duration: 0.6 }}
            className="mt-6 space-y-2"
          >
            {["No monthly fees — you own every file", "Written for real disputes: lien rights, teardown, damage claims", "Print on the front desk or edit in Word / Google Docs"].map((t, i) => (
              <li key={i} className="flex items-start gap-3 font-mono text-[11px] uppercase tracking-[0.14em] text-slate-300">
                <span className="mt-1 w-2 h-2 bg-blaze shrink-0" />
                {t}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-buy-complete-cta"
              onClick={() => buy("complete-shop-system")}
              disabled={loading !== null}
              className="inline-flex items-center gap-2.5 bg-blaze text-white font-display font-bold uppercase tracking-wide text-base px-7 py-4 clip-notch-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(249,115,22,0.4)] disabled:opacity-60"
            >
              {loading === "complete-shop-system" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
              Get the Complete System — $297
            </button>
            <button
              data-testid="hero-preview-button"
              onClick={() => scrollToId("preview")}
              className="inline-flex items-center gap-2 border border-slate-400/30 text-slate-200 font-display font-bold uppercase tracking-wide text-base px-7 py-4 transition-colors duration-200 hover:border-blaze/60 hover:text-blaze"
            >
              <FileText className="w-5 h-5" /> Preview the files
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15 }}
            className="mt-4 text-xs text-slate-500"
          >
            Just need the essentials?{" "}
            <button
              data-testid="hero-buy-starter-cta"
              onClick={() => buy("starter-kit")}
              className="text-blaze underline underline-offset-4 hover:text-orange-300"
            >
              Grab the Starter Kit for $67
            </button>
          </motion.p>
        </div>

        <div className="lg:col-span-5 relative" style={{ perspective: 1100 }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: EASE }}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative clip-notch border border-slate-400/15 bg-panel"
          >
            <img
              src={IMAGES.hero}
              alt="Mechanic torquing a wrench on an engine"
              className="w-full h-[380px] sm:h-[460px] object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-transparent" />
            <div className="spotlight absolute inset-0" />
            <div className="absolute top-4 left-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blaze opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blaze" />
              </span>
              System online
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
            style={{ x: cardX, y: cardY }}
            className="relative sm:absolute sm:-bottom-10 sm:-left-10 mt-4 sm:mt-0 w-full sm:w-[300px] border border-slate-400/15 bg-raised/95 backdrop-blur-md p-5 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-blaze">Kit inspector</span>
              <ShieldCheck className="w-4 h-4 text-blaze" />
            </div>
            <ul className="space-y-2">
              {FILES.map((f, i) => (
                <motion.li
                  key={f}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.1 + i * 0.1 }}
                  className="flex items-center gap-2 font-mono text-[10px] text-slate-300 truncate"
                >
                  <CheckSquare className="w-3.5 h-3.5 text-blaze shrink-0" />
                  {f}
                </motion.li>
              ))}
              <li className="font-mono text-[10px] text-slate-500 pt-1">+ 9 more in the Complete System</li>
            </ul>
          </motion.div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 mt-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 border border-slate-400/10 divide-x divide-y lg:divide-y-0 divide-slate-400/10 bg-panel/60">
          {STATS.map(([num, label], i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.6, ease: EASE }}
              className="p-6"
            >
              <div className="font-mono font-bold tracking-tighter text-2xl sm:text-3xl text-slate-50">{num}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
